import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Typography,
  Alert,
  CircularProgress,
  Box,
  Checkbox,
  FormControlLabel,
  Divider,
} from '@mui/material';
import { Share as ShareIcon } from '@mui/icons-material';
import { Friend, Routine, FetchWithAuth } from '../../types';
import { API_URL } from '../../utils';
import { useLanguage } from '../../i18n/LanguageContext';

interface ShareRoutineDialogProps {
  open: boolean;
  onClose: () => void;
  routine: Routine | null;
  fetchWithAuth: FetchWithAuth;
  onShareSuccess: () => void;
}

interface ShareableRoutine {
  id: number;
  name: string;
  description?: string;
  totalDays: number;
  totalExercises: number;
}

const ShareRoutineDialog: React.FC<ShareRoutineDialogProps> = ({
  open,
  onClose,
  routine,
  fetchWithAuth,
  onShareSuccess,
}) => {
  const { t } = useLanguage();
  const [friends, setFriends] = useState<Friend[]>([]);
  const [selectedFriends, setSelectedFriends] = useState<number[]>([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [friendsLoading, setFriendsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const sharableRoutine: ShareableRoutine | null = routine ? {
    id: routine.id!,
    name: routine.name,
    description: routine.description,
    totalDays: routine.days.length,
    totalExercises: routine.days.reduce((total, day) => total + day.exercises.length, 0)
  } : null;

  const loadFriends = useCallback(async () => {
    setFriendsLoading(true);
    try {
      const response = await fetchWithAuth(`${API_URL}/friends`);
      if (response.ok) {
        const data = await response.json();
        const acceptedFriends = data.filter((friend: Friend) => friend.status === 'accepted');
        setFriends(acceptedFriends);
      } else {
        throw new Error('Failed to load friends');
      }
    } catch (error) {
      console.error('Error loading friends:', error);
      setError(t('friends.loadError'));
    } finally {
      setFriendsLoading(false);
    }
  }, [fetchWithAuth, t]);

  // Load friends when dialog opens
  useEffect(() => {
    if (open) {
      loadFriends();
    }
  }, [open, loadFriends]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedFriends([]);
      setMessage('');
      setError(null);
      setSuccess(null);
    }
  }, [open]);

  const handleFriendToggle = (friendId: number) => {
    setSelectedFriends(prev => 
      prev.includes(friendId)
        ? prev.filter(id => id !== friendId)
        : [...prev, friendId]
    );
  };

  const handleShare = async () => {
    if (!routine || selectedFriends.length === 0) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const sharePromises = selectedFriends.map(friendId =>
        fetchWithAuth(`${API_URL}/routines/${routine.id}/share`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            friendId,
            message: message.trim() || undefined,
          }),
        })
      );

      const responses = await Promise.all(sharePromises);
      const results = await Promise.all(responses.map(r => r.json()));

      const successfulShares = results.filter(r => r.success);
      const failedShares = results.filter(r => !r.success);

      if (successfulShares.length === selectedFriends.length) {
        setSuccess(t('routines.shareSuccess', { count: successfulShares.length }));
        onShareSuccess();
        setTimeout(() => {
          onClose();
        }, 2000);
      } else if (successfulShares.length > 0) {
        setSuccess(t('routines.sharePartialSuccess', { 
          successful: successfulShares.length, 
          total: selectedFriends.length 
        }));
        if (failedShares.length > 0) {
          setError(failedShares[0].message || t('routines.shareError'));
        }
      } else {
        setError(failedShares[0]?.message || t('routines.shareError'));
      }
    } catch (error) {
      console.error('Error sharing routine:', error);
      setError(t('routines.shareError'));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    setSelectedFriends(selectedFriends.length === friends.length ? [] : friends.map(f => f.friend_id));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShareIcon />
          {t('routines.shareRoutine')}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {sharableRoutine && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {sharableRoutine.name}
            </Typography>
            {sharableRoutine.description && (
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {sharableRoutine.description}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              {t('routines.routineStats', {
                days: sharableRoutine.totalDays,
                exercises: sharableRoutine.totalExercises
              })}
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            {t('routines.selectFriends')}
          </Typography>
          
          {friendsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress />
            </Box>
          ) : friends.length === 0 ? (
            <Alert severity="info">
              {t('routines.noFriendsToShare')}
            </Alert>
          ) : (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedFriends.length === friends.length}
                    indeterminate={selectedFriends.length > 0 && selectedFriends.length < friends.length}
                    onChange={handleSelectAll}
                  />
                }
                label={t('routines.selectAll')}
                sx={{ mb: 1 }}
              />
              
              <List sx={{ maxHeight: 200, overflow: 'auto' }}>
                {friends.map((friend) => (
                  <ListItem key={friend.id} dense>
                    <ListItemText
                      primary={friend.friend_name}
                      secondary={friend.friend_email}
                    />
                    <ListItemSecondaryAction>
                      <Checkbox
                        checked={selectedFriends.includes(friend.friend_id)}
                        onChange={() => handleFriendToggle(friend.friend_id)}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </Box>

        <TextField
          fullWidth
          multiline
          rows={3}
          label={t('routines.shareMessage')}
          placeholder={t('routines.shareMessagePlaceholder')}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          sx={{ mb: 2 }}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleShare}
          variant="contained"
          disabled={loading || selectedFriends.length === 0 || !routine}
          startIcon={loading ? <CircularProgress size={20} /> : <ShareIcon />}
        >
          {loading ? t('common.sharing') : t('routines.share')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareRoutineDialog;